import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './LanguagePage.css';
import { AuthContext } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faComments,
  faFlagCheckered,
  faLink,
  faImage,
  faBoxOpen,
  faSpinner,
  faNoteSticky
} from '@fortawesome/free-solid-svg-icons';
import WordPackIntroModal from '../../components/WordPackIntroModal/WordPackIntroModal';
import WordPackModal from '../../components/WordPackModal/WordPackModal';
import ContextSelectionModal from '../../components/ContextSelectionModal/ContextSelectionModal';
import openAIService from '../../services/openAIService';
import DailyLimitModal from '../../components/DailyLimitModal/DailyLimitModal';

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const languageImages = {
  Spanish: {
    flag: '/images/mexico_flag.png',
    cover: '/images/spain_cover.jpg',
  },
  Japanese: {
    flag: '/images/icon_flag-ja.svg',
    cover: '/images/japanese-cover.webp',
  },
  Portuguese: {
    flag: '/images/icon_flag-pt.svg',
    cover: '/images/portugese-cover.jpg',
  },
  French: {
    flag: '/images/icon_flag-fr.svg',
    cover: '/images/french-cover.jpg',
  },
  German: {
    flag: '/images/icon_flag-de.svg',
    cover: '/images/german-cover.jpg',
  },
  Italian: {
    flag: '/images/icon_flag-it.svg',
    cover: '/images/italian-cover.jpg',
  },
  Mandarin: {
    flag: '/images/icon_flag-zh.svg',
    cover: '/images/mandarin-cover.jpg',
  },
  Arabic: {
    flag: '/images/icon_flag-ar.svg',
    cover: '/images/arabic-cover.jpg',
  },
  Cantonese: {
    flag: '/images/icon_flag-hk.png',
    cover: '/images/cantonese-cover.jpg',
  },
  Korean: {
    flag: '/images/icon_flag-ko.svg',
    cover: '/images/korean-cover.jpg',
  },
  Russian: {
    flag: '/images/icon_flag-ru.svg',
    cover: '/images/russian-cover.jpg',
  },
  Tagalog: {
    flag: '/images/icon_flag_pp.png',
    cover: '/images/tagalog-cover.jpg',
  },
  Greek: {
    flag: '/images/icon_flag_gr.png',
    cover: '/images/greek-cover.jpg',
  },
  Hindi: {
    flag: '/images/icon_flag_in.png',
    cover: '/images/hindi-cover.jpg',
  },
};



// Helper function to get translation parameters based on UI language
const getTranslationParams = (currentLanguage) => {
  const parameterMap = {
    es: { level: 'nivel', language: 'idioma' },
    fr: { level: 'niveau', language: 'langue' },
    it: { level: 'livello', language: 'lingua' },
    pt: { level: 'nível', language: 'idioma' },
    
    // Germanic languages
    de: { level: 'stufe', language: 'sprache' },
    
    // Slavic languages
    ru: { level: 'уровень', language: 'язык' },
    
    // Greek
    el: { level: 'επίπεδο', language: 'γλώσσα' },
    
    // East Asian languages
    ja: { level: 'レベル', language: '言語' },
    ko: { level: '수준', language: '언어' },
    zh: { level: '当前级别', language: '语言' },     // Mandarin
    'zh-CN': { level: '当前级别', language: '语言' }, // Mainland China
    'zh-HK': { level: '當前級別', language: '語言' }, // Hong Kong/Cantonese
    'zh-TW': { level: '當前級別', language: '語言' }, // Taiwan
    
    // South Asian languages
    hi: { level: 'स्तर', language: 'भाषा' },
    
    // Semitic languages
    ar: { level: 'مستوى', language: 'لغة' },
    
    // Austronesian languages
    tl: { level: 'antas', language: 'wika' }, // Tagalog
  };

  // Get the base language code or full code
  return parameterMap[currentLanguage] || 
         parameterMap[currentLanguage.split('-')[0]] || 
         { level: 'level', language: 'language' };
};

const videosByLanguage = {
  spanish: [
    { id: 'CTHoo_eIiIA', language: 'spanish' }, // Easy Spanish: Basic Spanish Phrases
    { id: 'ybfyLfI5Ml0', language: 'spanish' }, // Easy Spanish: Street Interviews
    { id: 'hpsgQkD27BA', language: 'spanish' }  // Easy Spanish: Daily Routine
  ],
  french: [
    { id: 'CmTYhv2F96k', language: 'french' },  // Easy French: Basic French Phrases
    { id: 'aMx0d42wzBs', language: 'french' },  // Easy French: Street Interviews Paris
    { id: 'w_9ynCt9nLc', language: 'french' }   // Easy French: Daily Life
  ],
  german: [
    { id: 'an232kkTEMk', language: 'german' },  // Easy German: Basic Phrases
    { id: 'G-WTBhyoK7o', language: 'german' },  // Easy German: Street Interviews Berlin
    { id: 'TDRNIkVE4bw', language: 'german' }   // Easy German: Daily Life
  ],
  italian: [
    { id: 'GFlG9liLM-4', language: 'italian' }, // Easy Italian: Basic Phrases
    { id: 'q2bUdfFVllI', language: 'italian' }, // Easy Italian: Street Interviews Rome
    { id: '8Pmz1TZ6OLc', language: 'italian' }  // Easy Italian: Daily Life
  ],
  portuguese: [
    { id: 'HWusYk1PGYY', language: 'portuguese' }, // Easy Portuguese: Basic Phrases
    { id: 'UcrxqVirLRU', language: 'portuguese' }, // Easy Portuguese: Street Interviews
    { id: 'CG-VLGlG7TY', language: 'portuguese' }  // Easy Portuguese: Daily Life
  ],
  japanese: [
    { id: 'JYEutL_zQQc', language: 'japanese' }, // Easy Japanese: Basic Phrases
    { id: 'kwMiWJAR6Ag', language: 'japanese' }, // Easy Japanese: Street Interviews Tokyo
    { id: 'tupRbmVM9Wc', language: 'japanese' }  // Easy Japanese: Daily Life
  ],
  mandarin: [
    { id: 'eKWrEV1fawc', language: 'mandarin' }, // Easy Mandarin: Basic Phrases
    { id: 'KLe98XxzDSg', language: 'mandarin' }, // Easy Mandarin: Street Interviews
    { id: 'A-oPzWRqtDw', language: 'mandarin' }  // Easy Mandarin: Daily Life
  ],
  korean: [
    { id: 'in3QD4BzUB4', language: 'korean' },   // Easy Korean: Basic Phrases
    { id: 'Aeufi2J3m6o', language: 'korean' },   // Easy Korean: Street Interviews Seoul
    { id: 'HjutPoNDVrI', language: 'korean' }    // Easy Korean: Daily Life
  ],
  russian: [
    { id: 'prSfxdmjNzE', language: 'russian' },  // Easy Russian: Basic Phrases
    { id: 'AlUMYm5YExA', language: 'russian' },  // Easy Russian: Street Interviews
    { id: 'P2O8HWoMEX0', language: 'russian' }   // Easy Russian: Daily Life
  ],
  arabic: [
    { id: 'NQnsUR6FFFg', language: 'arabic' },   // Easy Arabic: Basic Phrases
    { id: 'oDnbqK34pas', language: 'arabic' },   // Easy Arabic: Street Interviews
    { id: 'cT9aDNrqe30', language: 'arabic' }    // Easy Arabic: Daily Life
  ],
  greek: [
    { id: '8VKdTtEm_EI', language: 'greek' },
    { id: 'm0RH3xg_WiQ', language: 'greek' },
    { id: 'i9ofxsTm0sI', language: 'greek' }
  ],
  cantonese: [
    { id: 'm03X0MZBQzI', language: 'cantonese' },
    { id: 'lsqmMsH-cTs', language: 'cantonese' },
    { id: 'QelN8DL_6yU', language: 'cantonese' }
  ],
  tagalog: [
    { id: 'COCDy-fArT', language: 'tagalog' },
    { id: 'UOoRkoYLIpQ', language: 'tagalog' },
  ],
  hindi: [
    { id: 'uz08VvPEC-o', language: 'hindi' },
    { id: 'YCkY_yo_wgU', language: 'hindi' },
  ],
  // If no specific videos for a language, use trending videos as fallback
  default: [
    { id: 'CTHoo_eIiIA', language: 'spanish' },
    { id: 'CmTYhv2F96k', language: 'french' },
    { id: 'KLe98XxzDSg', language: 'german' }
  ]
};

function LanguagePage() {
  const { t, i18n } = useTranslation();
  const currentUILanguage = i18n.language;
  const currentLanguageParams = getTranslationParams(currentUILanguage);
  const { language } = useParams();
  const { user } = useContext(AuthContext);
  const [level, setLevel] = useState('');
  const [userLanguageLevel, setUserLanguageLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vocabGamesPlayed, setVocabGamesPlayed] = useState(0);
  const [storyChainGamesPlayed, setStoryChainGamesPlayed] = useState(0);
  const [isOpeningPack, setIsOpeningPack] = useState(false);
  const [showPackModal, setShowPackModal] = useState(false);
  const [currentPhrases, setCurrentPhrases] = useState([]);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [hasSeenIntro, setHasSeenIntro] = useState(() => {
    const seen = localStorage.getItem('wordPackIntroSeen');
    return seen === 'true';
  });
  const [isClosing, setIsClosing] = useState(false);
  const [pastPacks, setPastPacks] = useState([]);
  const [showContextModal, setShowContextModal] = useState(false);
  const [selectedContext, setSelectedContext] = useState('');
  const [showDailyLimitModal, setShowDailyLimitModal] = useState(false);
  const [nextAvailableTime, setNextAvailableTime] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [notes, setNotes] = useState(() => {
    const savedNotes = localStorage.getItem(`language_notes_${language}`);
    return savedNotes || '';
  });
  const navigate = useNavigate();

  const languageData = languageImages[language.charAt(0).toUpperCase() + language.slice(1).toLowerCase()] || {};

  // Get current videos and create thumbnail URLs
  const currentVideos = videosByLanguage[language.toLowerCase()] || videosByLanguage.default;
  const videoIds = currentVideos.map(video => video.id);
  const videoThumbnails = videoIds.map((id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`);

  useEffect(() => {
    if (user && user.id) {
      const fetchUserLanguageLevel = async () => {
        try {
          const response = await fetch(`${apiUrl}/user_language_levels?user_id=${user.id}`);
          const data = await response.json();
          if (data.success) {
            const userLevel = data.language_levels.find(l => l.language.toLowerCase() === language.toLowerCase());
            setUserLanguageLevel(userLevel ? userLevel.level : null);
          } else {
            console.error('Failed to fetch user language levels:', data.message);
          }
        } catch (error) {
          console.error('Error fetching user language levels:', error);
        } finally {
          setLoading(false);
        }
      };

      const fetchGamesPlayedByLanguage = async (gameName, language, setGamesPlayedState) => {
        try {
          const response = await fetch(`${apiUrl}/games_played/${user.id}/${gameName}/${language}`);
          const data = await response.json();
          if (data.success) {
            setGamesPlayedState(data.games_played);
          } else {
            console.error(`Failed to fetch ${gameName} games played:`, data.message);
          }
        } catch (error) {
          console.error(`Error fetching ${gameName} games played:`, error);
        }
      };

      fetchUserLanguageLevel();
      fetchGamesPlayedByLanguage('Vocabulary Race', language, setVocabGamesPlayed);
      fetchGamesPlayedByLanguage('Story Chain', language, setStoryChainGamesPlayed);
    } else {
      setLoading(false); // Ensure loading stops if no user
    }
  }, [user, language]);

  useEffect(() => {
    if (user && user.id) {
      const fetchPastPacks = async () => {
        try {
          const response = await fetch(`${apiUrl}/word_packs/${user.id}/${language}`);
          const data = await response.json();
          if (data.success) {
            setPastPacks(data.packs);
          }
        } catch (error) {
          console.error('Error fetching past packs:', error);
        }
      };
      fetchPastPacks();
    }
  }, [user, language]);

  useEffect(() => {
    document.body.classList.add('language-page-body');
    return () => {
      document.body.classList.remove('language-page-body');
    };
  }, []);

  const handleLevelChange = (selectedLevel) => {
    setLevel(selectedLevel);

    if (!user || !user.id) {
      console.error('User not logged in');
      return;
    }

    fetch(`${apiUrl}/update_language_level`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: user.id,
        language: language.charAt(0).toUpperCase() + language.slice(1),
        level: selectedLevel,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log('Language level updated successfully', data);
          setUserLanguageLevel(selectedLevel);
        } else {
          console.error('Failed to update language level', data.message);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const handleConnect = () => {
    navigate(`/connect/${language}`);
  };

  const handleChat = () => {
    navigate(`/chat/${language}`);
  };

  const handleVocabRace = () => {
    navigate(`/vocabularyrace/${language}`);
  };

  const handleStoryChain = () => {
    navigate(`/storychain/${language}`);
  };

  const handleOpenPack = async () => {
    if (isOpeningPack || isClosing || !user) return; // Added user check

    try {
      if (!user.is_pro) {
        const response = await fetch(
          `${apiUrl}/word_packs/can_generate?user_id=${user.id}&language=${language}`
        );
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error('Failed to check pack availability');
        }

        if (!data.can_generate) {
          const nextAvailable = new Date(data.next_available);
          const formattedTime = nextAvailable.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
          });
          setNextAvailableTime(formattedTime);
          setShowDailyLimitModal(true);
          return;
        }
      }

      if (!hasSeenIntro) {
        setShowIntroModal(true);
        return;
      }

      setShowContextModal(true);
    } catch (error) {
      console.error('Error checking pack availability:', error);
      alert(t('languagePage.errors.pack.availability'));
    }
  };

  const handleContextSelection = async (context) => {
    setShowContextModal(false);
    setIsOpeningPack(true);
    setSelectedContext(context);

    if (!user) { // Added user check
        alert("Please log in to generate word packs.");
        setIsOpeningPack(false);
        return;
    }

    try {
      // Re-check availability just before generating
      const checkResponse = await fetch(
        `${apiUrl}/word_packs/can_generate?user_id=${user.id}&language=${language}`
      );
      const checkData = await checkResponse.json();

      if (!checkResponse.ok || !checkData.success) {
        throw new Error('Failed to check pack availability');
      }

      if (!checkData.can_generate && !user.is_pro) { // Check if still allowed
        const nextAvailable = new Date(checkData.next_available);
        const formattedTime = nextAvailable.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit'
        });
        setNextAvailableTime(formattedTime);
        setShowDailyLimitModal(true);
        setIsOpeningPack(false); // Stop loading state
        return;
      }

      const phrases = await openAIService.generatePhrasesForContext(
        language,
        context,
        userLanguageLevel || 'A1'
      );

      if (!Array.isArray(phrases) || !phrases.every(p => p.phrase && p.translation)) {
        throw new Error('Invalid phrases format received');
      }

      const saveResponse = await fetch(`${apiUrl}/word_packs/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          language,
          phrases,
          level: userLanguageLevel || 'A1',
          context
        }),
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.message || 'Failed to save word pack');
      }

      setCurrentPhrases(phrases);
      setShowPackModal(true);

      // Update past packs state immediately
      setPastPacks(prev => [{
        phrases,
        level: userLanguageLevel || 'A1',
        context,
        opened_at: new Date().toISOString()
      }, ...prev].slice(0, 10));

    } catch (error) {
      console.error('Error generating or saving phrases:', error);
      alert(error.message || 'Sorry, there was an error opening the pack.');
    } finally {
      setIsOpeningPack(false);
    }
  };

  const handleStartConversation = () => {
    if (!user) { // Added user check
        alert("Please log in to start a conversation.");
        return;
    }
    if (!userLanguageLevel) {
      alert(t('languagePage.conversation.selectLevel'));
      return;
    }
    navigate(`/conversation/${language}`, {
      state: { level: userLanguageLevel }
    });
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex((prev) =>
      prev === (currentVideos.length - 1) ? 0 : prev + 1
    );
  };

  const handlePrevVideo = () => {
    setCurrentVideoIndex((prev) =>
      prev === 0 ? currentVideos.length - 1 : prev - 1 // Corrected logic
    );
  };

  const handleNotesChange = (event) => {
    const newNotes = event.target.value;
    setNotes(newNotes);
    localStorage.setItem(`language_notes_${language}`, newNotes);
  };

  if (loading) {
    return <div className="loading-placeholder"></div>; // Or a proper loading spinner
  }

  return (
    <div className="language-page">
      <button
        className="back-button fade-in"
        onClick={() => navigate('/dashboard')}
      >
        {t('languagePage.buttons.back')}
      </button>

      <div
        className="cover-image fade-in"
        style={{ backgroundImage: `url(${languageData.cover})` }}
      ></div>

      <div className="flag-container fade-in">
        <img src={languageData.flag} alt={`${language} flag`} className="language-flag" />
      </div>

      {userLanguageLevel ? (
        <div className="connect-section fade-in">
          <h2 className="connect-header">
            {t('languagePage.level.current', {
              level: userLanguageLevel,
              [currentLanguageParams.level]: userLanguageLevel, // Dynamic param
              // Keep other language fallbacks if needed, or rely on dynamic param
              Level: userLanguageLevel, // Default fallback
            })}
          </h2>
          <button className="connect-button" onClick={handleConnect}>
            {t('languagePage.buttons.videoChat')}
          </button>
          <button className="chat-button" onClick={handleChat}>
            {t('languagePage.buttons.chatRoom')}
          </button>
        </div>
      ) : (
        <div className="level-selection fade-in">
          <h2>{t('languagePage.level.select')}</h2>
          <p className="level-helper-text">{t('languagePage.level.helper')}</p>
          <div className="level-buttons">
            {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((levelOption) => (
              <div key={levelOption} className="level-button-container">
                <button
                  className={`level-button ${level === levelOption ? 'selected' : ''}`}
                  onClick={() => handleLevelChange(levelOption)}
                >
                  {levelOption}
                </button>
                <div className="level-tooltip">
                  <strong>{t(`languagePage.level.descriptions.${levelOption}.name`)}</strong> - {t(`languagePage.level.descriptions.${levelOption}.description`)} ({t(`languagePage.level.descriptions.${levelOption}.vocabulary`)})
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="content-layout">
        <div className="notion-section fade-in">
          <div className="notion-link-card" onClick={handleStartConversation}>
            <div className="notion-link-icon">
              <FontAwesomeIcon icon={faComments} />
            </div>
            <div className="notion-link-content">
              <div className="notion-link-title">{t('languagePage.conversation.section.cardTitle')}</div>
              <div className="notion-link-description">{t('languagePage.conversation.section.description')}</div>
            </div>
          </div>

          <div className="notion-link-card" onClick={handleOpenPack} style={{ cursor: isOpeningPack ? 'wait' : 'pointer' }}>
            <div className="notion-link-icon">
              <FontAwesomeIcon icon={isOpeningPack ? faSpinner : faBoxOpen} spin={isOpeningPack} />
            </div>
            <div className="notion-link-content">
              <div className="notion-link-title">
                {isOpeningPack ? t("languagePage.wordpack.generating") : t("languagePage.daily.word.pack")}
              </div>
              <div className="notion-link-description">
                {t("languagePage.wordpack.description", { language: language.charAt(0).toUpperCase() + language.slice(1) })}
              </div>
            </div>
          </div>

          <div className="notion-section-title">{t('languagePage.immersiveLearning.title')}</div>

          <div className="notion-link-card" onClick={handleVocabRace}>
            <div className="notion-link-icon">
              <FontAwesomeIcon icon={faFlagCheckered} />
            </div>
            <div className="notion-link-content">
              <div className="notion-link-title">{t('languagePage.minigames.vocabRace.name')}</div>
              <div className="notion-link-description">{t('languagePage.minigames.vocabRace.played', { count: vocabGamesPlayed })}</div>
            </div>
          </div>

          <div className="notion-link-card" onClick={handleStoryChain}>
            <div className="notion-link-icon">
              <FontAwesomeIcon icon={faLink} />
            </div>
            <div className="notion-link-content">
              <div className="notion-link-title">{t('languagePage.minigames.storyChain.name')}</div>
              <div className="notion-link-description">{t('languagePage.minigames.storyChain.played', { count: storyChainGamesPlayed })}</div>
            </div>
          </div>

          <div className="notion-link-card coming-soon-notion">
            <div className="notion-link-icon">
              <FontAwesomeIcon icon={faImage} />
            </div>
            <div className="notion-link-content">
              <div className="notion-link-title">{t('languagePage.minigames.picturePerfect.name')}</div>
              <div className="notion-link-description">{t('languagePage.minigames.picturePerfect.played', { count: 0 })}</div>
            </div>
            <div className="coming-soon-banner-notion">{t('languagePage.minigames.picturePerfect.comingSoon')}</div>
          </div>
        </div>

        <div className="language-notes-section">
          <h3 className="notes-header">
            <FontAwesomeIcon icon={faNoteSticky} style={{ marginRight: '8px' }} />
            {t("languagePage.notes.title", "Quick Notes")}
          </h3>
          <textarea
            className="notes-textarea"
            value={notes}
            onChange={handleNotesChange}
            placeholder={t("languagePage.notes.placeholder", "Write your language learning notes here...")}
          />
        </div>
      </div>

      <div className="comprehensible-input-section">
        <div className="comprehensible-input-video-container">
          <img
            src={videoThumbnails[currentVideoIndex]}
            alt={t('languagePage.comprehensibleInput.videoAlt')}
            className="comprehensible-input-thumbnail"
          />
          <div className="comprehensible-input-nav-arrows">
            <button
              className="comprehensible-input-nav-button"
              onClick={handlePrevVideo}
              aria-label="Previous video"
            >
              ←
            </button>
            <button
              className="comprehensible-input-nav-button"
              onClick={handleNextVideo}
              aria-label="Next video"
            >
              →
            </button>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${videoIds[currentVideoIndex]}`}
            target="_blank"
            rel="noopener noreferrer"
            className="comprehensible-input-watch-button"
          >
            {t('languagePage.comprehensibleInput.watchButton')}
          </a>
        </div>
        <div className="comprehensible-input-description">
          {t('languagePage.comprehensibleInput.description')}
        </div>
      </div>

      <WordPackIntroModal
        isOpen={showIntroModal}
        onClose={() => {
          setShowIntroModal(false);
          setHasSeenIntro(true);
          localStorage.setItem('wordPackIntroSeen', 'true');
          // Automatically proceed to context selection after closing intro
          if (user) { // Check user exists before showing context modal
             setShowContextModal(true);
          }
        }}
        language={language}
      />

      <ContextSelectionModal
        isOpen={showContextModal}
        onClose={() => setShowContextModal(false)}
        onSelectContext={handleContextSelection}
      />

      <WordPackModal
        isOpen={showPackModal}
        onClose={() => {
          setIsClosing(true);
          setShowPackModal(false);
          setTimeout(() => {
            setIsClosing(false);
          }, 300); // Match animation duration if any
        }}
        phrases={currentPhrases}
        language={language}
        context={selectedContext}
      />

      <DailyLimitModal
        isOpen={showDailyLimitModal}
        onClose={() => setShowDailyLimitModal(false)}
        nextAvailableTime={nextAvailableTime}
      />

      <div className="past-packs-section">
        <h3 className="past-packs-header">
          <FontAwesomeIcon icon={faHistory} style={{ marginRight: '8px' }} />
          {t("languagePage.past.packs")}
        </h3>
        <div className="past-packs-container">
          {pastPacks.map((pack, index) => (
            <div 
              key={index} 
              className="past-pack-card"
              onClick={() => {
                setCurrentPhrases(pack.phrases);
                setSelectedContext(pack.context);
                setShowPackModal(true);
              }}
            >
              <div className="past-pack-date">
                {new Date(pack.opened_at).toLocaleDateString()}
              </div>
              <div className="past-pack-level">
                Level {pack.level}
              </div>
              {pack.context && (
                <div className="past-pack-context">
                  {pack.context}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LanguagePage;
